import React, { useEffect, useMemo, useRef } from 'react';
import brandBookTemplate from './brand-book.html?raw';
import './brand-book.css';

// Brand Book route — permanent iframe of the standalone portal at /brand/
// per docs/BRAND_PORTAL_PASSDOWN.md open decision #1. Route-level auth is
// enforced by <ProtectedRoute roles={['admin','brand-viewer']}> in App.tsx;
// the iframe's own /brand/ request flows through nginx's auth_request to
// /api/auth/brand-check (bp_token cookie).
//
// Two concerns baked into this component:
//
// 1. Height math (see brand-book.css). The container spans exactly from
//    where it renders in the viewport down to viewport bottom. Static
//    `100vh - <nav-h>` math doesn't work because the nav-bar wraps on
//    narrow viewports. Effect below measures the container's viewport
//    top on mount, on window resize, and on any body reflow, then writes
//    the value to --brand-book-top-offset for the CSS to consume.
//
// 2. Cache-busting iframe src. Chrome and Safari cached /brand/index.html
//    + /brand/content.js inside the iframe across the pre-fix → post-fix
//    save endpoint change; a parent-page hard refresh did NOT flush the
//    iframe. Only "Empty Cache and Hard Reload" cleared it. New nginx
//    Cache-Control: no-cache headers should prevent recurrence, but we
//    also append a ?v=<render-time> param to the iframe src so a
//    freshly-mounted portal never even asks the cache. Cheap belt-and-
//    suspenders — service workers regressing or headers being
//    misconfigured can't smuggle stale bytes through.
export function BrandBookPage(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);

  // Stable within a single mount — refreshing the parent page bumps it.
  // Deliberately not derived from window.location.reload or state; a
  // component-lifetime buster is enough because the whole portal reloads
  // when React remounts (route change or full page refresh).
  const cacheBust = useMemo(() => Date.now(), []);
  const iframeSrc = `/brand/?v=${cacheBust}`;
  const html = useMemo(() => brandBookTemplate.replace('{{IFRAME_SRC}}', iframeSrc), [iframeSrc]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const recompute = (): void => {
      const rect = el.getBoundingClientRect();
      // rect.top is the container's Y in the viewport. Clamp to
      // non-negative — during scroll the container's top could go
      // negative, but we render at page top and scrolling is contained
      // within the iframe, so this stays >= 0 in practice.
      const topOffset = Math.max(0, rect.top);
      el.style.setProperty('--brand-book-top-offset', `${topOffset}px`);
    };

    recompute();
    window.addEventListener('resize', recompute);
    // Watch for anything above us reflowing — nav bar wrapping when
    // the user's role set changes visible links is the canonical case.
    const ro = new ResizeObserver(recompute);
    ro.observe(document.body);

    return () => {
      window.removeEventListener('resize', recompute);
      ro.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="brand-book-page"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
